import {
  createContext,
  PropsWithChildren,
  RefObject,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import ReCAPTCHA from "react-google-recaptcha"

interface AnonymousNewslettersContextShape {
  email: string
  setEmail: (email: string) => void
  isSubscribed: boolean
  setIsSubscribed: (value: boolean) => void
  recaptchaRef: RefObject<ReCAPTCHA>
}

/**
 * Context for managing anonymous user state in newsletters page.
 * Handles email input, subscription status, and reCAPTCHA execution.
 */
const AnonymousNewslettersContext = createContext<AnonymousNewslettersContextShape>({
  email: "",
  setEmail: () => {},
  isSubscribed: false,
  setIsSubscribed: () => {},
  recaptchaRef: { current: null },
})

export const AnonymousNewslettersProvider = ({ children }: PropsWithChildren) => {
  const [email, setEmail] = useState("")
  const [isSubscribed, setIsSubscribed] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const contextValue = useMemo(
    () => ({
      email,
      setEmail,
      isSubscribed,
      setIsSubscribed,
      recaptchaRef,
    }),
    [email, isSubscribed],
  )

  return (
    <AnonymousNewslettersContext.Provider value={contextValue}>
      {children}
    </AnonymousNewslettersContext.Provider>
  )
}

export const useAnonymousNewslettersContext = () => useContext(AnonymousNewslettersContext)
